import React, { useState, useEffect, useMemo } from 'react';
import apiService from '../../services/apiService';
import { PageLoader } from '../../components/PageLoader';
import { Rocket, Calendar, Lightbulb, CheckCircle, Clock, Hourglass, FolderKanban, Layers, X, Wand2, ShieldCheck } from 'lucide-react';
import MissionControlView from './MissionControlView';
import IdeaHangarView from './IdeaHangarView';
import Modal from '../../components/Modal';

// --- Reusable Components ---
const Card = ({ children, className = '' }) => ( <div className={`bg-slate-800 border border-slate-700 rounded-lg shadow-md p-4 ${className}`}>{children}</div> );
const Button = ({ children, onClick, className = '', variant = 'primary', icon: Icon, disabled = false }) => {
    const variants = {
        primary: 'bg-green-500/20 text-green-300 hover:bg-green-500/30 disabled:bg-slate-700 disabled:text-slate-500',
        secondary: 'bg-slate-600 text-slate-100 hover:bg-slate-500',
    };
    return (
        <button onClick={onClick} disabled={disabled} className={`flex items-center justify-center gap-2 px-4 py-2 rounded-md font-semibold text-sm transition-all duration-200 ${variants[variant]} ${className}`}>
            {Icon && <Icon size={16} />}
            {children}
        </button>
    );
};

// --- NEW: Deployment Modal for "Ready for Takeoff" ---
const DeploymentModal = ({ task, onClose, onDeploy }) => {
    const [isDeploying, setIsDeploying] = useState(false);
    const [summary, setSummary] = useState('');

    const handleDeploy = async () => {
        setIsDeploying(true);
        try {
            const result = await onDeploy(task._id, {}); // Pass details if needed
            setSummary(result.summary || 'Deployment completed successfully.');
        } catch (error) {
            setSummary(`Deployment failed: ${error.message}`);
        } finally {
            setIsDeploying(false);
        }
    };

    return (
        <Modal isOpen={true} onClose={onClose} title={`Deploy Task: ${task.title}`}>
            <div className="max-h-[70vh] overflow-y-auto p-1">
                <div className="bg-slate-900/50 p-4 rounded-lg mb-4">
                    <h3 className="font-semibold text-white mb-2">Task Details</h3>
                    <p className="text-sm text-slate-300">{task.description}</p>
                </div>
                <div className="bg-slate-900/50 p-4 rounded-lg">
                    <h3 className="font-semibold text-white mb-2">Logic Panel</h3>
                    <p className="text-sm text-slate-400 mb-4">AI will use the appropriate integration to execute this task. Review details and confirm deployment.</p>
                    <Button onClick={handleDeploy} disabled={isDeploying || summary} icon={isDeploying ? Hourglass : ShieldCheck}>
                        {isDeploying ? 'Deploying...' : (summary ? 'Deployed' : 'Confirm Deployment')}
                    </Button>
                </div>
                {summary && (
                    <div className="mt-4 p-4 bg-slate-700 rounded-lg">
                        <h4 className="font-semibold text-white mb-2">Deployment Summary</h4>
                        <p className="text-sm text-slate-300 whitespace-pre-wrap">{summary}</p>
                    </div>
                )}
            </div>
        </Modal>
    );
};

// --- REVISED: Main View for the "Command Center" Tab ---
const CommandCenter = ({ tasks, ideas, onNavigate, onDeployTask }) => {
    const [taskToDeploy, setTaskToDeploy] = useState(null);

    const approvedTasks = useMemo(() => tasks.filter(t => t.status === 'Pending Approval'), [tasks]);
    const inProgressTasks = useMemo(() => tasks.filter(t => t.status === 'In-Progress'), [tasks]);
    const scheduledTasks = useMemo(() => 
        tasks.filter(t => t.status === 'Scheduled')
             .sort((a, b) => new Date(a.date) - new Date(b.date))
             .slice(0, 10), 
    [tasks]);

    return (
        <>
            {taskToDeploy && <DeploymentModal task={taskToDeploy} onClose={() => setTaskToDeploy(null)} onDeploy={onDeployTask} />}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fade-in">
                <div className="lg:col-span-2 flex flex-col gap-6">
                    <Card className="flex-grow">
                        <h2 className="flex items-center gap-3 text-xl font-bold text-white mb-4"><Rocket className="text-green-400" /> Ready for Takeoff</h2>
                        <div className="space-y-3">
                            {approvedTasks.length > 0 ? (
                                approvedTasks.map(task => (
                                    <div key={task._id} className="bg-slate-900/50 p-3 rounded-lg flex items-center justify-between gap-3">
                                        <div>
                                            <p className="text-slate-100 font-medium">{task.title}</p>
                                            <p className="text-xs text-slate-400">From: {task.origin}</p>
                                        </div>
                                        <Button variant="primary" icon={ShieldCheck} onClick={() => setTaskToDeploy(task)}>Deploy</Button>
                                    </div>
                                ))
                            ) : <p className="text-slate-400 text-center py-4">Approval queue is empty. Good job!</p>}
                        </div>
                    </Card>
                    <Card>
                        <h2 className="flex items-center gap-3 text-xl font-bold text-white mb-4"><Calendar className="text-purple-400" /> In-Flight Timeline</h2>
                        <div className="space-y-4">
                            <div>
                                <h3 className="font-semibold text-slate-300 mb-2">In Progress ({inProgressTasks.length})</h3>
                                {inProgressTasks.length > 0 ? inProgressTasks.map(task => (
                                    <div key={task._id} className="flex items-center gap-3 text-slate-300 text-sm pl-2 border-l-2 border-brand-500">
                                        <Hourglass size={14} className="text-brand-400 animate-spin" />
                                        <span>{task.title}</span>
                                    </div>
                                )) : <p className="text-slate-500 text-sm pl-2">No tasks in progress.</p>}
                            </div>
                            <div>
                                <h3 className="font-semibold text-slate-300 mb-2">Upcoming ({scheduledTasks.length})</h3>
                                {scheduledTasks.length > 0 ? scheduledTasks.map(task => (
                                    <div key={task._id} className="flex items-center justify-between text-slate-300 text-sm pl-2 border-l-2 border-purple-500 my-1">
                                        <div className="flex items-center gap-3">
                                            <Clock size={14} className="text-purple-400" />
                                            <span>{task.title}</span>
                                        </div>
                                        <span className="text-xs font-mono bg-slate-700 px-2 py-1 rounded">{new Date(task.date).toLocaleDateString()}</span>
                                    </div>
                                )) : <p className="text-slate-500 text-sm pl-2">No tasks scheduled.</p>}
                            </div>
                        </div>
                    </Card>
                </div>
                <div className="lg:col-span-1">
                    <Card className="h-full">
                        <h2 className="flex items-center gap-3 text-xl font-bold text-white mb-4"><Lightbulb className="text-yellow-400" /> Idea Hangar Snapshot</h2>
                        <div className="space-y-3">
                            {ideas.slice(0, 4).map((idea, index) => (
                                <div key={index} className="bg-slate-700/70 p-3 rounded-lg">
                                    <p className="text-slate-200 text-sm">{idea.title}: {idea.description}</p>
                                </div>
                            ))}
                        </div>
                        <Button onClick={() => onNavigate('ideaHangar')} className="w-full mt-4" variant="secondary">View All Ideas</Button>
                    </Card>
                </div>
            </div>
        </>
    );
};
export default function FlightDeckView() {
    const [activeView, setActiveView] = useState('commandCenter');
    const [tasks, setTasks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [ideas, setIdeas] = useState([]);
    const [isIdeasLoading, setIsIdeasLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchAllData = async () => {
            setIsLoading(true);
            setIsIdeasLoading(true);
            try {
                const [tasksData, ideasData] = await Promise.all([
                    apiService.fetchFlightDeckTasks(),
                    apiService.fetchNewIdeas()
                ]);
                setTasks(tasksData || []);
                const key = Object.keys(ideasData)[0];
                setIdeas(ideasData[key] || []);
            } catch (err) {
                setError('Failed to load Flight Deck data.');
                console.error(err);
            } finally {
                setIsLoading(false);
                setIsIdeasLoading(false);
            }
        };
        fetchAllData();
    }, []);

    const handleDeployTask = async (taskId, details) => {
        try {
            const result = await apiService.executeTaskDeployment(taskId, details);
            // Refresh tasks after deployment
            const tasksData = await apiService.fetchFlightDeckTasks();
            setTasks(tasksData || []);
            return result;
        } catch (error) {
            console.error("Deployment failed:", error);
            throw error;
        }
    };

    const navItems = [
        { id: 'commandCenter', label: 'Command Center', icon: FolderKanban },
        { id: 'ideaHangar', label: 'Idea Hangar', icon: Lightbulb },
        { id: 'missionControl', label: 'Mission Control', icon: Layers },
    ];

    const renderView = () => {
        if (isLoading) return <PageLoader text="Loading Flight Deck..." />;
        if (error) return <p className="text-red-400 p-6">{error}</p>;

        switch (activeView) {
            case 'commandCenter':
                return <CommandCenter tasks={tasks} ideas={ideas} onNavigate={setActiveView} onDeployTask={handleDeployTask} />;
            case 'ideaHangar':
                return <IdeaHangarView />;
            case 'missionControl':
                return <MissionControlView />;
            default:
                return <CommandCenter tasks={tasks} ideas={ideas} onNavigate={setActiveView} onDeployTask={handleDeployTask} />;
        }
    };

    return (
        <div>
            <header className="flex flex-col md:flex-row justify-between md:items-center gap-4 border-b border-slate-700 pb-4 mb-6">
                <h1 className="text-3xl font-bold text-white">Flight Deck</h1>
                <div className="bg-slate-800 p-1 rounded-lg flex items-center gap-1 flex-wrap">
                    {navItems.map(item => (
                        <button
                            key={item.id}
                            onClick={() => setActiveView(item.id)}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors duration-200 ${
                                activeView === item.id ? 'bg-teal-700 text-white' : 'text-slate-300 hover:bg-slate-700'
                            }`}
                        >
                            <item.icon size={16} />
                            {item.label}
                        </button>
                    ))}
                </div>
            </header>
            {renderView()}
        </div>
    );
}